import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputText, Button, TextValueAccessor } from '@shui/angular';
import { catchError, EMPTY, tap } from 'rxjs';
import { AgGridAngular } from 'ag-grid-angular';
import {
    NumberInputCellComponent,
    ShSelectComponent,
    ShSelectOption,
    shsThemeDark
} from '@shs-digi-care-monorepo/frontend-lib';
import type { CellValueChangedEvent, ColDef } from 'ag-grid-community';
import { createInstrumentOnlyFormGroup, InstrumentOnlyFormGroup } from '../../models/instrument-only.model';
import { ProductLine, productLineLabelKey } from '../../../shared/models/temp.model';
import { OneDxPlatformService } from '../../../shared/services/onedx-platform.service';
import { OneDxCustomerInformation, OneDxMaterialItem } from '../../../shared/models/onedx.model';

@Component({
    selector: 'app-instrument-only',
    templateUrl: './instrument-only.component.html',
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        InputText,
        Button,
        TextValueAccessor,
        AgGridAngular,
        ShSelectComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class InstrumentOnlyForm {
    public showContinue = input<boolean>(true);
    public continue = output<void>();

    private oneDxService = inject(OneDxPlatformService);
    private destroyRef = inject(DestroyRef);
    private translate = inject(TranslateService);

    public formGroup: FormGroup<InstrumentOnlyFormGroup> = createInstrumentOnlyFormGroup();

    protected loading = signal(false);
    public customerInformation = signal<OneDxCustomerInformation | null>(null);
    public rowData = signal<OneDxMaterialItem[]>([]);
    private resultsSection = viewChild<ElementRef>('resultsSection');

    protected productLineOptions: ShSelectOption[] = Object.values(ProductLine).map((value) => ({
        value,
        label: productLineLabelKey(value)
    }));

    protected canQuery = (): boolean => {
        const { productLine, soldTo, shipTo } = this.formGroup.value;
        return !!productLine && !!soldTo && !!shipTo;
    };

    protected queryOneDx(): void {
        const { productLine, soldTo, shipTo } = this.formGroup.value;
        if (!productLine || !soldTo || !shipTo) return;

        this.loading.set(true);
        setTimeout(() => {
            this.resultsSection()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        this.oneDxService
            .getDealInformation({
                productLine,
                soldToNumber: soldTo,
                shipToNumber: shipTo
            })
            .pipe(
                tap((data) => {
                    this.customerInformation.set(data.customerInformation);
                    this.rowData.set(data.materials);
                    this.formGroup.controls.materials.setValue([...data.materials]);
                    this.loading.set(false);
                }),
                catchError(() => {
                    this.loading.set(false);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    protected colDefs: ColDef[] = [
        { field: 'productLine', headerName: this.translate.instant('iro.grid.product-line') },
        { field: 'description', headerName: this.translate.instant('iro.grid.description') },
        { field: 'smn', headerName: this.translate.instant('iro.grid.smn') },
        { field: 'kitSizeDescription', headerName: this.translate.instant('iro.grid.kit-size-description') },
        { field: 'kitSize', headerName: this.translate.instant('iro.grid.kit-size') },
        {
            field: 'annualTestVolume',
            headerName: this.translate.instant('iro.grid.annual-test-volume'),
            cellRenderer: NumberInputCellComponent,
            cellStyle: {
                padding: '0'
            }
        },
        {
            field: 'inventoryAmt',
            headerName: this.translate.instant('iro.grid.inventory-amount'),
            cellRenderer: NumberInputCellComponent,
            cellStyle: {
                padding: '0'
            }
        },
        {
            field: 'implementationQuantity',
            headerName: this.translate.instant('iro.grid.implementation-quantity'),
            cellRenderer: NumberInputCellComponent,
            cellStyle: {
                padding: '0'
            }
        },
        {
            field: 'orderQuantity',
            headerName: this.translate.instant('iro.grid.order-quantity'),
            cellRenderer: NumberInputCellComponent,
            cellStyle: {
                padding: '0'
            }
        },
        { field: 'unitPrice', headerName: this.translate.instant('iro.grid.unit-price') },
        { field: 'totalPrice', headerName: this.translate.instant('iro.grid.total-price') }
    ];

    protected gridOptions = {
        theme: shsThemeDark
    };

    protected onContinue(): void {
        this.continue.emit();
    }

    protected onCellValueChanged(event: CellValueChangedEvent): void {
        const allRowData: OneDxMaterialItem[] = [];
        event.api.forEachNode((node) => allRowData.push(node.data));
        this.formGroup.controls.materials.setValue(allRowData);
    }
}
